import { BookingServiceClient } from 'fitbeat.api-clients';
import {
  HomeWorkoutSequence, Instructor, UserProfile, UserWorkoutSession,
} from 'fitbeat.gym-core';
import {
  CardioOrResistance, EnabledFeatures, GymTypes, IEnabledFeatures, ILiveStreamOptions,
  BaseUserProfile,
} from 'fitbeat.models';
import React, { FunctionComponent, useEffect } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import Orientation from 'react-native-orientation-locker';
import { connect } from 'react-redux';
import { enabledFeaturesSelector } from '../../../auth/auth-selectors';
import { OpenClassList } from '../../../Bookings/components/OpenClassList';
import {
  BookingFlowFooter,
  WhatYouNeedForWokoutText,
  WorkoutFormat,
  WorkoutSettingsSummary,
  WorkoutTypeWithBodyGroupText,
} from '../../../Bookings/components';
import { shouldShowCusomiseWorkoutSettingsInBooking } from '../../../Bookings/selectors';
import { Button, ButtonSize, ButtonTypes } from '../../../components';
import { getApiConfig, build } from '../../../config';
import Theme from '../../../theme';
import { TileSection } from '../../../ui';
import { StatusBarComponent } from '../../../ui/components/StatusBar';
import { AuthTokenBuilder } from '../../../utils';
import { defaultScale } from '../../../utils/scaling';
import {
  inviteFriendsPressed,
  openSetupScreen,
  preWorkoutSummaryCancelWorkoutPressed,
  preWorkoutSummaryScreenClosed, setBetaUserBenchAndDumbbellsAction,
  setBetaUserOptionAction,
  startWorkout,
  workoutTypeChanged,
} from '../../actions';
import { IAppStore } from '../../reducer';
import {
  classStartsInTextSelector,
  getInstructorNameSelector,
  workoutIsReadyToStart,
} from '../../selectors';
import { IAudioHelper } from '../../utils';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import { initiateGroupCall } from '../VideoCalling';
import { BoostYourExperience } from './BoostYourExperience';
import { PreWorkoutProgressLoading } from './PreWorkoutProgressLoading';
import { PreworkoutSetupInstructorHeader } from './PreworkoutSetupInstructorHeader';
import { TypeOfWorkout } from '../../../Bookings/components/TypeOfWorkout';
import { GymResolver } from '../../../services';
import { LivestreamOptionsSummary } from '../../../Bookings/steps/LivestreamOptionsSummary';

const { isElite } = build.variant;

export interface IProps {
  userProfile: UserProfile;
  enabledFeatures: IEnabledFeatures;
  isGroupWorkout: boolean;
  homeWorkoutSession: HomeWorkoutSequence | null;
  startWorkoutPress: () => void;
  openDeviceSetupScreen: (screen: DeviceSetupScreens) => void;
  preloadingComplete: boolean;
  leaveHomeWorkout: () => void;
  cancelBookingPressed: () => void;
  homeWorkoutLoadingError: string | null;
  bookingServiceClient: BookingServiceClient;
  instructor: Instructor | null;
  instructorName: string;
  remainingSecondsInSequence: number | null;
  audioHelper: IAudioHelper;
  loadingShareLink: boolean;
  invitePressed: () => void;
  classStartsInText: string | null;
  showCusomiseWorkoutSettings: boolean;
  enableInstructorChange: boolean;
  livestreamOptions?: ILiveStreamOptions;
  betaFeatureChanging: boolean;
  t(key: string, options?: any): string;
  workoutTypeSelected(cardioOrResistance: CardioOrResistance): void;
  setBetaUserOption(): void;
}

const PreWorkoutSetupComponent: FunctionComponent<IProps> = ({
  t,
  userProfile,
  enabledFeatures,
  homeWorkoutSession,
  isGroupWorkout,
  startWorkoutPress,
  leaveHomeWorkout,
  homeWorkoutLoadingError,
  openDeviceSetupScreen,
  preloadingComplete,
  bookingServiceClient,
  instructor,
  instructorName,
  remainingSecondsInSequence,
  loadingShareLink,
  invitePressed,
  classStartsInText,
  cancelBookingPressed,
  showCusomiseWorkoutSettings,
  enableInstructorChange,
  workoutTypeSelected,
  livestreamOptions,
  betaFeatureChanging,
  setBetaUserOption,
}) => {
  if (!homeWorkoutSession && !homeWorkoutLoadingError) {
    return (
      <ActivityIndicator
        size='small'
        animating
        color={Theme.PRIMARY}
        style={styles.container}
      />
    );
  }
  if (homeWorkoutLoadingError) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{homeWorkoutLoadingError}</Text>
          <Text style={styles.errorText}>{t('preworkoutCriticalErrorAction')}</Text>
          <Button
            title='Continue'
            fontColor={Theme.WHITE}
            onPress={leaveHomeWorkout}
            buttonType={ButtonTypes.NORMAL}
            containerStyle={styles.errorButton}
            size={ButtonSize.MEDIUM}
          />
        </View>
      </View>
    );
  }
  useEffect(() => {
    Orientation.lockToPortrait();
  }, []);

  const gym = GymResolver.getByName(homeWorkoutSession.gym.name);
  const showGymEquipment = gym.type === GymTypes.Home && EnabledFeatures.canSelectEquipment(enabledFeatures);

  return (
    <View style={styles.container}>
      <StatusBarComponent barStyle='light-content' />
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          <PreworkoutSetupInstructorHeader
            openInstructorSetup={() => openDeviceSetupScreen(DeviceSetupScreens.Instructor)}
            instructor={instructor}
            leaveHomeWorkout={leaveHomeWorkout}
            homeWorkout={homeWorkoutSession}
            t={t}
            instructorName={instructorName}
            isGroupWorkout={isGroupWorkout}
            enableInstructorChange={enableInstructorChange}
          />

          <BoostYourExperience
            bookingServiceClient={bookingServiceClient}
            homeWorkoutSession={homeWorkoutSession}
            userProfile={userProfile}
          />

          {showCusomiseWorkoutSettings
            && (
            <TileSection heading={t('typeOfWorkout')}>
              <TypeOfWorkout
                workoutTypeSelected={workoutTypeSelected}
                workoutSettings={homeWorkoutSession.workoutSettings}
              />
            </TileSection>
            )}

          {homeWorkoutSession && UserWorkoutSession.isStreamingWorkoutAndIsHost(homeWorkoutSession)
            && (
            <View style={styles.tile}>
              <LivestreamOptionsSummary
                livestreamOptions={livestreamOptions}
                startTimeOfFirstExercise={homeWorkoutSession.startTimeOfFirstExercise}
                t={t}
                displayedAtStartOfWorkout
              />
            </View>
            )}
          <TileSection heading={t('yourWorkoutSentenceCase')}>
            <WorkoutSettingsSummary
              t={t}
              gym={gym}
              gender={userProfile.gender}
              workoutSettings={homeWorkoutSession.workoutSettings}
              personaliseWorkout={() => openDeviceSetupScreen(DeviceSetupScreens.MyWorkout)}
              showGymEquipment={showGymEquipment}
              showCusomiseWorkoutSettings={showCusomiseWorkoutSettings}
            />
          </TileSection>
          {
            isGroupWorkout
            && (
            <TileSection heading={t('joiningThisClass')}>
              <OpenClassList
                t={t}
                workoutId={homeWorkoutSession.workoutId}
                hideHeading
              />
              <Button
                title={t('inviteYourFriends')}
                isLoading={loadingShareLink}
                onPress={invitePressed}
                buttonType={ButtonTypes.TRANSPARENT}
                containerStyle={styles.sectionButton}
              />
            </TileSection>
            )
          }
          <TileSection>
            <WorkoutTypeWithBodyGroupText
              t={t}
              gym={gym}
              workoutSettings={homeWorkoutSession.workoutSettings}
            />
          </TileSection>
          { !isElite
          && (
          <TileSection>
            <WhatYouNeedForWokoutText
              t={t}
              equipment={homeWorkoutSession?.workoutSettings?.equipment}
            />
            <Button
              title={t('seeCastingInstructions')}
              onPress={() => openDeviceSetupScreen(DeviceSetupScreens.ScreenCasting)}
              buttonType={ButtonTypes.TRANSPARENT}
              containerStyle={styles.sectionButton}
            />
          </TileSection>
          )}
          <TileSection>
            <WorkoutFormat t={t} formatType={homeWorkoutSession.workoutType} />
          </TileSection>
          <TileSection>
            <Button
              size={ButtonSize.MEDIUM}
              buttonType={ButtonTypes.TRANSPARENT}
              title={t('homeWorkouts:cancelWorkout')}
              onPress={cancelBookingPressed}
              containerStyle={{ height: 'auto' }}
            />
          </TileSection>
          {BaseUserProfile.isFeatureEnabledForUser('beta', userProfile) && (
            <TileSection heading='Beta user options'>
              {betaFeatureChanging
                ? <ActivityIndicator />
                : (
                  <Button
                    size={ButtonSize.MEDIUM}
                    buttonType={ButtonTypes.OUTLINE}
                    title='Set current workout to boxing'
                    onPress={setBetaUserOption}
                  />
                )}
            </TileSection>
          )}
        </ScrollView>
      </View>
      <BookingFlowFooter>
        {
          classStartsInText
          && (
          <Text style={styles.infoText}>
            {t(classStartsInText, { remainingSecondsInSequence })}
          </Text>
          )
        }
        <View style={styles.footer}>
          {
            preloadingComplete
            && (
            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              title={isGroupWorkout
                ? t('homeWorkouts:joinClass')
                : t('homeWorkouts:startWorkoutDeviceSetup')}
              onPress={startWorkoutPress}
              containerStyle={styles.nextContainer}
            />
            )
          }
          {
            !preloadingComplete
            && <PreWorkoutProgressLoading />
          }
        </View>
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  const bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder(),
  );
  return {
    isGroupWorkout: state.homeWorkouts.isGroupWorkout,
    homeWorkoutSession: state.homeWorkouts.homeWorkoutSession,
    homeWorkoutLoadingError: state.homeWorkouts.homeWorkoutLoadingError,
    preloadingComplete: workoutIsReadyToStart(state),
    bookingServiceClient,
    instructor: state.homeWorkouts.selectedInstructor,
    instructorName: getInstructorNameSelector(state),
    loadingShareLink: state.homeWorkouts.loadingShareLink,
    classStartsInText: classStartsInTextSelector(state),
    enabledFeatures: enabledFeaturesSelector(state),
    showCusomiseWorkoutSettings: shouldShowCusomiseWorkoutSettingsInBooking(state.homeWorkouts.homeWorkoutSession),
    livestreamOptions: state.homeWorkouts.livestreamOptions,
    betaFeatureChanging: state.homeWorkouts.betaFeatureChangeLoading,
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder(),
  );
  const { userProfile } = ownProps;

  const initiateCall = (groupCallPairedId: string): void => dispatch(
    initiateGroupCall(bookingServiceClient, ownProps.t, userProfile, groupCallPairedId),
  );

  return {
    startWorkoutPress: () => dispatch(startWorkout(bookingServiceClient, ownProps.audioHelper, initiateCall)),
    openDeviceSetupScreen: (screen: DeviceSetupScreens) => dispatch(openSetupScreen(screen)),
    workoutTypeSelected: (cardioOrResistance) => dispatch(
      (workoutTypeChanged({ cardioOrResistance, screen: DeviceSetupScreens.WorkoutTypeWarning })),
    ),
    leaveHomeWorkout: () => dispatch(preWorkoutSummaryScreenClosed(bookingServiceClient)),
    cancelBookingPressed: () => dispatch(preWorkoutSummaryCancelWorkoutPressed(bookingServiceClient)),
    invitePressed: () => dispatch(inviteFriendsPressed(bookingServiceClient, userProfile, ownProps.t)),
    // If adding more functions to the beta section consider adding these actions as an array and looping
    setBetaUserOption: () => dispatch(setBetaUserOptionAction('boxing')),
    // when creating individual buttons
  };
};

export const PreWorkoutSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common', 'homeWorkouts'], { wait: true })(PreWorkoutSetupComponent),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.GREY_LIGHT,
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
    paddingBottom: defaultScale(13),
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 1,
  },
  settingContainer: {
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: Theme.GREY_LIGHT,
  },
  tile: {
    backgroundColor: Theme.WHITE,
    marginTop: defaultScale(10),
    paddingHorizontal: defaultScale(18),
    paddingVertical: defaultScale(22),
  },
  infoText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK,
    paddingHorizontal: defaultScale(20),
    paddingBottom: defaultScale(10),
  },
  sectionButton: {
    marginTop: defaultScale(15),
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    paddingHorizontal: defaultScale(10),
    paddingBottom: defaultScale(10),
    ...Theme.textStyle.heading2,
  },
  errorButton: {
    width: '90%',
  },
  cancelWorkoutButton: {
    margin: defaultScale(15),
  },
  scrollContainer: {
    paddingBottom: defaultScale(10),
  },
});
